import React from 'react';
import {
  Typography,
  SkeletonBlock,
  SkeletonContent,
} from '@one-thd/sui-atomic-components';
import { InstantCheckoutDrawer } from '../InstantCheckoutDrawer/InstantCheckoutDrawer';
import { InstantCheckoutAddressCard } from './InstantCheckoutAddressCard';
import { SetupButton } from './SetupButton';
import { StatusMessage } from './StatusMessage';
import { useDrawer } from '../../hooks/use-drawer';
import { useAddress } from '../../hooks/use-address';
import { useInstantCheckoutStatus } from '../../hooks/use-instant-checkout-status';
import {
  ADD_DELIVERY_ADDRESS,
  EDIT_ADDRESS,
  SELECT_DELIVERY_ADDRESS,
} from '../../models/views';
import { TAlertType } from '../../models/common/alert';
import { InstantCheckoutAlert } from '../shared/InstantCheckoutAlert';
import { usePayments } from '../../hooks/use-payments';
import { track } from '../../analytics/analytics';
import { INSTANT_CHECKOUT_OVERLAY } from '../../constants/analytics';

const buttonStyles = 'sui-w-full md:sui-w-1/2 xl:sui-w-1/3';

export const InstantCheckoutAddressSection = () => {
  const { hasAddresses, addressLoading, defaultAddress } = useAddress();
  const { defaultPayment, paymentsLoading } = usePayments();
  const { openDrawer, hideDrawer } = useDrawer();
  const {
    alertMessage,
    isEnrolledInInstantCheckout,
    profileLoading,
    updateProfileLoading,
    timesLoaded,
  } = useInstantCheckoutStatus();

  const renderComponent = () => {
    const editView = EDIT_ADDRESS;
    const selectView = SELECT_DELIVERY_ADDRESS;
    const addDeliveryView = ADD_DELIVERY_ADDRESS;
    const view = hasAddresses() ? selectView : addDeliveryView;

    if (defaultAddress) {
      return (
        <div className="sui-grid md:sui-grid-cols-9">
          <InstantCheckoutAddressCard
            onEdit={() => {
              track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout edit delivery address',
              });
              openDrawer(InstantCheckoutDrawer, {
                hideDrawer,
                initialView: editView,
                flowName: 'EDIT_DELIVERY_DETAILS',
              });
            }}
            onChangeAddress={() => {
              track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout select delivery address',
              });
              openDrawer(InstantCheckoutDrawer, {
                hideDrawer,
                initialView: selectView,
                flowName: 'CHANGE_WHERE_YOU_GET_IT',
              });
            }}
            disabled={!isEnrolledInInstantCheckout}
            address={defaultAddress}
          />
        </div>
      );
    }

    if (defaultPayment) {
      return (
        <div data-testid="checkout-btn-setup">
          <div className="sui-pb-8">
            <StatusMessage disabled={!isEnrolledInInstantCheckout} />
          </div>
          <div className={buttonStyles}>
            <SetupButton
              onSetup={() => {
                const initialAction = hasAddresses() ? 'select' : 'add';

                track(INSTANT_CHECKOUT_OVERLAY, {
                  value: `instant checkout ${initialAction} delivery address`,
                });
                openDrawer(InstantCheckoutDrawer, {
                  hideDrawer,
                  initialView: view,
                  flowName: 'SETUP_WHERE_YOU_GET_IT',
                });
              }}
            />
          </div>
        </div>
      );
    }

    if (isEnrolledInInstantCheckout || timesLoaded !== 1) {
      return <StatusMessage disabled={!isEnrolledInInstantCheckout} />;
    }
    return null;
  };

  const loading =
    addressLoading || paymentsLoading || profileLoading || updateProfileLoading;
  const shouldShowComponent =
    defaultPayment ||
    defaultAddress ||
    isEnrolledInInstantCheckout ||
    timesLoaded !== 1;
  return (
    <>
      {!loading && shouldShowComponent && (
        <div data-testid="checkout-address-card-container">
          <div className="sui-mb-4">
            <Typography variant="h3" weight="display">
              Delivery Address
            </Typography>
          </div>
          {alertMessage?.alertType === TAlertType.IC_ADDRESS_UPDATE && (
            <div className="sui-col-span-full sui-my-2">
              <InstantCheckoutAlert />
            </div>
          )}
          {renderComponent()}
        </div>
      )}

      {updateProfileLoading && (
        <SkeletonContent disablePadding>
          <SkeletonBlock height={10} />
          <SkeletonBlock aspect="wide" height={40} />
        </SkeletonContent>
      )}
    </>
  );
};
